import { type PaymentOrder, type SolidgateRequestPaymentResponse, SolidgatePaymentStatus } from '@/types/payment';

import { useAppDispatch } from '@/hooks/redux';
import { resetPaymentDetails, setOrderToPay, setPaymentStatus } from '@/store/payment/paymentSlice';
import { playNotification, playPaid } from '@/shared/features/player/player';

/**
 * Use this wrapper if you want to use all modals to less import statements.
 */
export const useSolidgateModals = () => {
  // Modals
  // -- payment status modal (pending, success, error)
  const paymentStatusModal = useSolidgatePaymentStatusModal();
  // -- parent modal
  const paymentModal = useSolidgatePaymentModal();
  return { paymentStatusModal, paymentModal };
};

/**
 * Main modal with payment form
 */
export const useSolidgatePaymentModal = () => {
  const dispatch = useAppDispatch();

  // Close the payment modal and reset merchant data and order
  const close = () => {
    dispatch(resetPaymentDetails());
  };

  // Get merchant data and set order to pay
  const open = async ({ order, paymentDetails }: { order: PaymentOrder; paymentDetails: SolidgateRequestPaymentResponse }) => {
    dispatch(setOrderToPay({ paymentDetails, order }));
  };

  return { close, open };
};

/**
 * Payment status modal to display payment status (pending, success, error).
 */
export const useSolidgatePaymentStatusModal = () => {
  const dispatch = useAppDispatch();
  const paymentModal = useSolidgatePaymentModal();

  const error = (code?: string) => {
    paymentModal.close();
    dispatch(setPaymentStatus({ id: SolidgatePaymentStatus.Failed, code }));
    return playNotification();
  };

  const success = (order: PaymentOrder) => {
    paymentModal.close();
    dispatch(setPaymentStatus({ idOrder: order.id, id: SolidgatePaymentStatus.Success }));
    return playPaid();
  };

  const pending = () => {
    paymentModal.close();
    dispatch(setPaymentStatus({ id: SolidgatePaymentStatus.Pending }));
  };

  const close = () => {
    dispatch(setPaymentStatus({ id: undefined }));
  };

  return { close, error, success, pending };
};
